/**
 * CompanyLogoUploader — Gestión del logotipo de la empresa.
 *
 * Permite agregar, reemplazar y eliminar el logo. El archivo se sube al servicio
 * de archivos (`URL_FILES`) y en la BD solo se persiste el UUID resultante
 * mediante `PUT /v1/empresa/logo`.
 *
 * El logo se usa en la cabecera del PDF de cotizaciones, por eso se restringe a
 * PNG/JPG: son los formatos que `@react-pdf/renderer` sabe incrustar.
 */

import React, { useState } from "react";
import { Button, Flex, Popconfirm, Spin, Typography, Upload, theme } from "antd";
import {
  DeleteOutlined,
  PictureOutlined,
  SwapOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { URL_FILES } from "../../../constans/apis";
import type { ApiError } from "../../../types/apiResponse";
import { useUploadFile } from "../../../lib/files/hooks/useUploadFile";
import openNotification from "../../../utils/openNotification";
import { useCompanyLogoImage } from "../hooks/useCompanyLogoImage";
import { useDeleteCompanyLogo } from "../hooks/useDeleteCompanyLogo";
import { useUpdateCompanyLogo } from "../hooks/useUpdateCompanyLogo";

const { Text } = Typography;

const ACCEPTED_TYPES = ["image/png", "image/jpeg"];
const MAX_SIZE_MB = 2;

export interface CompanyLogoUploaderProps {
  /** UUID guardado en `Companies.uuid_logo`. Vacío o ausente = sin logo. */
  uuidLogo?: string;
  /** Solo lectura: muestra el logo sin acciones de edición. */
  readOnly?: boolean;
  size?: number;
}

const CompanyLogoUploader: React.FC<CompanyLogoUploaderProps> = ({
  uuidLogo,
  readOnly = false,
  size = 140,
}) => {
  const { token } = theme.useToken();
  const [isUploading, setIsUploading] = useState(false);

  const hasLogo = Boolean(uuidLogo?.trim());

  const { data: logoSrc, isLoading: isLoadingImage } = useCompanyLogoImage(uuidLogo);
  const { mutateAsync: uploadFileMutation } = useUploadFile();
  const { mutateAsync: saveLogoMutation, isPending: isSaving } = useUpdateCompanyLogo();
  const { mutate: deleteLogoMutation, isPending: isDeleting } = useDeleteCompanyLogo();

  const isBusy = isUploading || isSaving || isDeleting;

  const handleBeforeUpload = async (file: File): Promise<boolean> => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      openNotification("error", "Formato no válido", "El logo debe ser PNG o JPG.");
      return false;
    }

    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      openNotification(
        "error",
        "Archivo demasiado grande",
        `El logo no debe exceder ${MAX_SIZE_MB} MB.`
      );
      return false;
    }

    setIsUploading(true);
    try {
      const resFile = await uploadFileMutation({
        file,
        endpoint: URL_FILES,
        token: TOKEN_FILES,
      });

      const uuid = resFile.data?.UUID_file;
      if (resFile.status !== 201 || !uuid) {
        openNotification("error", "Error", resFile.message || "No se pudo subir el logo");
        return false;
      }

      await saveLogoMutation(uuid);
      openNotification("success", "Éxito", "Logotipo actualizado correctamente");
    } catch (error) {
      // `apiClient` rechaza con objetos planos `{ message }`, no con Error.
      const message = (error as ApiError | Error | undefined)?.message;
      openNotification("error", "Error", message || "No se pudo guardar el logo");
    } finally {
      setIsUploading(false);
    }

    // Siempre false: la subida se hace manualmente, no la maneja antd.
    return false;
  };

  const handleDelete = () => {
    deleteLogoMutation(undefined, {
      onSuccess: () => {
        openNotification("success", "Éxito", "Logotipo eliminado correctamente");
      },
      onError: (error) => {
        openNotification("error", "Error", error.message);
      },
    });
  };

  const frameStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: token.borderRadiusLG,
    border: `1px ${hasLogo ? "solid" : "dashed"} ${token.colorBorder}`,
    background: token.colorFillAlter,
    overflow: "hidden",
    flexShrink: 0,
  };

  return (
    <Flex vertical align="center" gap={12}>
      <Flex align="center" justify="center" style={frameStyle}>
        {isLoadingImage ? (
          <Spin />
        ) : hasLogo && logoSrc ? (
          <img
            src={logoSrc}
            alt="Logotipo de la empresa"
            style={{ maxWidth: "86%", maxHeight: "86%", objectFit: "contain" }}
          />
        ) : (
          <Flex vertical align="center" gap={6} style={{ padding: 12, textAlign: "center" }}>
            <PictureOutlined style={{ fontSize: 28, color: token.colorTextQuaternary }} />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Sin logotipo
            </Text>
          </Flex>
        )}
      </Flex>

      {!readOnly && (
        <>
          <Flex gap={8} wrap justify="center">
            <Upload
              accept={ACCEPTED_TYPES.join(",")}
              showUploadList={false}
              maxCount={1}
              beforeUpload={handleBeforeUpload}
              disabled={isBusy}
            >
              <Button
                icon={hasLogo ? <SwapOutlined /> : <UploadOutlined />}
                loading={isUploading || isSaving}
                disabled={isBusy}
              >
                {hasLogo ? "Cambiar logo" : "Subir logo"}
              </Button>
            </Upload>

            {hasLogo && (
              <Popconfirm
                title="Eliminar logotipo"
                description="Las cotizaciones dejarán de mostrarlo. ¿Continuar?"
                okText="Eliminar"
                cancelText="Cancelar"
                okButtonProps={{ danger: true }}
                onConfirm={handleDelete}
              >
                <Button danger icon={<DeleteOutlined />} loading={isDeleting} disabled={isBusy}>
                  Eliminar
                </Button>
              </Popconfirm>
            )}
          </Flex>

          <Text type="secondary" style={{ fontSize: 11, textAlign: "center" }}>
            PNG o JPG, máximo {MAX_SIZE_MB} MB. Se muestra en el PDF de las cotizaciones.
          </Text>
        </>
      )}
    </Flex>
  );
};

export default CompanyLogoUploader;
